import React, { ChangeEvent, FC, FormEvent, useState } from 'react';
import { useBuyerProfileActions, useBuyerProfiles, useProfileShares, useTeamConnections } from '../../../hooks/api/Team/useTeam';
import {
  buildProfilePayload, BuyerProfile, BuyerProfileValues, emptyBuyerProfile, getErrorMessages, ProfileField,
  PROFILE_SECTIONS,
} from '../../../utility/team';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const FieldInput: FC<{ field: ProfileField; value: BuyerProfileValues[string]; onChange: (name: string, value: string | boolean) => void }> = ({ field, value, onChange }) => {
  const id = `buyer_profile_${field.name}`;
  const handle = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    onChange(field.name, field.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value);
  const text = value === null || value === undefined ? '' : String(value);

  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-x-2 sm:col-span-2">
        <input id={id} type="checkbox" checked={Boolean(value)} onChange={handle} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
        <label htmlFor={id} className="text-sm text-gray-900">{field.label}</label>
      </div>
    );
  }

  return (
    <div className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-900">
        {field.label}{field.required && <span className="text-red-600"> *</span>}
      </label>
      {field.type === 'select' && (
        <select id={id} className={inputClass} value={text} onChange={handle}>
          <option value="">Choose...</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {field.type === 'textarea' && <textarea id={id} rows={3} className={inputClass} value={text} onChange={handle} />}
      {(field.type === 'text' || field.type === 'number' || field.type === 'date') && (
        <input
          id={id}
          type={field.type}
          step={field.type === 'number' ? 'any' : undefined}
          className={inputClass}
          value={text}
          onChange={handle}
        />
      )}
      {field.help && <p className="mt-1 text-xs text-gray-500">{field.help}</p>}
    </div>
  );
};

const ProfileForm: FC<{
  initial: BuyerProfileValues;
  profileId?: string;
  buyerId?: string;
  onDone: () => void;
}> = ({ initial, profileId, buyerId, onDone }) => {
  const { create, update } = useBuyerProfileActions(buyerId);
  const [ values, setValues ] = useState<BuyerProfileValues>(initial);
  const mutation = profileId ? update : create;

  const setValue = (name: string, value: string | boolean) => setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = buildProfilePayload(values);
    try {
      if (profileId) {
        await update.mutateAsync({ id: profileId, values: payload });
      } else {
        await create.mutateAsync(payload);
      }
      onDone();
    } catch {
      // the error is shown below from the mutation state
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-8">
      <div className="sm:max-w-md">
        <label htmlFor="buyer_profile_label" className="block text-sm font-medium text-gray-900">
          Profile name <span className="text-red-600">*</span>
        </label>
        <input id="buyer_profile_label" className={inputClass} placeholder="Personal, My LLC..." value={String(values.label ?? '')} onChange={(e) => setValue('label', e.target.value)} />
        <div className="mt-2 flex items-center gap-x-2">
          <input id="buyer_profile_is_default" type="checkbox" checked={Boolean(values.is_default)} onChange={(e) => setValue('is_default', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
          <label htmlFor="buyer_profile_is_default" className="text-sm text-gray-900">Use as my default profile</label>
        </div>
      </div>

      {PROFILE_SECTIONS.map((section) => (
        <fieldset key={section.title}>
          <legend className="text-sm font-semibold text-gray-900">{section.title}</legend>
          <div className="mt-3 grid gap-4 sm:max-w-3xl sm:grid-cols-2">
            {section.fields.map((field) => (
              <FieldInput key={field.name} field={field} value={values[field.name]} onChange={setValue} />
            ))}
          </div>
        </fieldset>
      ))}

      {profileId && !buyerId && <ProfileConnections profileId={profileId} />}

      {mutation.error && <ul className="text-sm text-red-600">{getErrorMessages(mutation.error).map((m) => <li key={m}>{m}</li>)}</ul>}
      <div className="flex gap-x-3">
        <button type="submit" disabled={mutation.isPending || !String(values.label ?? '').trim()} className={primaryButton}>Save profile</button>
        <button type="button" className={secondaryButton} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
};

// Bottom of a saved profile: which connected agents may see and use it for contracts.
export const ProfileConnections: FC<{ profileId: string }> = ({ profileId }) => {
  const { data: agents, isLoading, toggle } = useProfileShares(profileId);

  return (
    <fieldset data-testid="profile-connections">
      <legend className="text-sm font-semibold text-gray-900">Connections</legend>
      <p className="mt-1 text-sm text-gray-700">
        Choose which connected agents can see this profile and pick it when they make contracts for you.
      </p>
      {!isLoading && agents.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">You have no connected agents yet. Invite one from the Team page.</p>
      )}
      <ul className="mt-2 divide-y divide-gray-100 sm:max-w-3xl">
        {agents.map((agent) => (
          <li key={agent.agent_id} className="flex items-center justify-between gap-x-4 py-2">
            <div className="text-sm">
              <p className="font-medium text-gray-900">{agent.name}</p>
              <p className="text-gray-500">
                {agent.shared ? 'Using this profile' : 'Not shared'}
                {agent.contracts_made > 0 && ` · ${agent.contracts_made} contract${agent.contracts_made === 1 ? '' : 's'} made with it`}
              </p>
            </div>
            <label className="flex items-center gap-x-2 text-sm text-gray-900">
              <input
                type="checkbox"
                aria-label={`Share with ${agent.name}`}
                checked={agent.shared}
                disabled={toggle.isPending}
                onChange={(e) => toggle.mutate({ agentId: agent.agent_id, shared: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              Share
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
};

const BuyerProfilePage: FC = () => {
  const { data: connections } = useTeamConnections();
  const [ buyerId, setBuyerId ] = useState<string | undefined>(undefined);
  const { data: profiles, isLoading } = useBuyerProfiles(buyerId);
  const { remove } = useBuyerProfileActions(buyerId);
  const [ editing, setEditing ] = useState<{ id?: string; values: BuyerProfileValues } | null>(null);

  // Buyers a licensed professional can manage profiles for (active connections where I am the agent).
  const connectedBuyers = connections.filter((c) => c.status === 'active' && c.my_role === 'agent');

  const handleBuyerChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setBuyerId(e.target.value || undefined);
    setEditing(null);
  };

  return (
    <div className="px-2">
      <h1 className="text-base font-semibold leading-6 text-gray-900">Buyer Profiles</h1>
      <p className="mt-2 text-sm text-gray-700">
        A buyer profile holds the details and defaults filled into every auto-contract: the buyer, earnest money,
        option period, title company and more. You can keep several, such as a personal one and an LLC. Items marked
        <span className="text-red-600"> *</span> are needed before a contract can be generated.
      </p>

      {connectedBuyers.length > 0 && (
        <div className="mt-4 sm:max-w-sm">
          <label htmlFor="profile_owner" className="block text-sm font-medium text-gray-900">Profiles for</label>
          <select id="profile_owner" className={inputClass} value={buyerId ?? ''} onChange={handleBuyerChange}>
            <option value="">Me</option>
            {connectedBuyers.map((c) => <option key={c.id} value={c.other_user.id}>{c.other_user.name}</option>)}
          </select>
        </div>
      )}

      {editing ? (
        <ProfileForm
          key={editing.id ?? 'new'}
          initial={editing.values}
          profileId={editing.id}
          buyerId={buyerId}
          onDone={() => setEditing(null)}
        />
      ) : (
        <>
          <ul className="mt-6 divide-y divide-gray-100 sm:max-w-3xl">
            {!isLoading && profiles.length === 0 && <li className="py-2 text-sm text-gray-500">No profiles yet.</li>}
            {profiles.map((profile: BuyerProfile) => (
              <li key={profile.id} className="flex items-center justify-between gap-x-4 py-3">
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{profile.label}{profile.is_default && ' (default)'}</p>
                  <p className={profile.ready_for_contracts ? 'text-green-700' : 'text-yellow-800'}>
                    {profile.ready_for_contracts
                      ? 'Ready for contracts'
                      : `Still needed: ${(profile.missing_required_fields as unknown as string[]).join(', ')}`}
                  </p>
                </div>
                <div className="flex gap-x-2">
                  <button type="button" className={secondaryButton} onClick={() => setEditing({ id: profile.id, values: profile })}>Edit</button>
                  {!buyerId && (
                  <button
                    type="button"
                    className={secondaryButton}
                    onClick={() => { if (window.confirm(`Delete the profile "${profile.label}"?`)) remove.mutate(profile.id); }}
                  >
                    Delete
                  </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {buyerId ? (
            <p className="mt-4 text-sm text-gray-500">Only profiles this buyer has shared with you appear here.</p>
          ) : (
          <button type="button" className={`${primaryButton} mt-4`} onClick={() => setEditing({ values: emptyBuyerProfile() })}>
            Add a profile
          </button>
          )}
        </>
      )}
    </div>
  );
};

export default BuyerProfilePage;
